import React, { useState } from 'react';
import { ArrowDownIcon, ChevronDownIcon } from 'lucide-react';
import { SectionHeading } from './SectionHeading';
import { volumes } from '../data/volumes';
import { heroImage, systemFlow } from '../data/copy';
import { accentBg, accentBorder, accentText } from '../utils/accent';
import { optimizedImage, optimizedSrcSet } from '../utils/image';
export function VolumesSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <section id="volumenes" className="defer-paint w-full scroll-mt-6 bg-paper py-14 sm:py-20" aria-labelledby="volumes-heading" data-id="element-217" data-mp="3g2v9rag7r2pc">
      <div className="mx-auto max-w-4xl px-6" data-id="element-218" data-mp="3pvkihxkdq2gz">
        <SectionHeading eyebrow="Il sistema completo" title={<span id="volumes-heading" data-id="element-220" data-mp="3l42k9h9qqfzp">
              Il sistema visuale che organizza tutto questo
            </span>} data-id="element-219" data-mp="3pwc72ofk6zxr" />

        <img src={optimizedImage(heroImage, 720)} srcSet={optimizedSrcSet(heroImage, [480, 720, 960])} sizes="(min-width: 640px) 448px, calc(100vw - 48px)" alt="PALLAMANO 360™ completo: 6 volumi tecnici, piano della sessione su tablet e 5 bonus operativi." className="mx-auto mt-6 w-full max-w-md" width={1240} height={1240} loading="lazy" decoding="async" data-id="element-221" data-mp="2xdfasrk6fib1" />

        <p className="mx-auto mt-3 max-w-prose text-center font-sans text-base leading-relaxed text-ink-muted" style={{
        fontSize: "14px"
      }} data-id="element-222" data-mp="2j5k23jdi1kj5">
          6 volumi tecnici + 72 sessioni da 60 minuti + 5 bonus operativi.
        </p>

        <div className="mx-auto mt-10 max-w-3xl text-center" data-id="element-223" data-mp="1qhxwk5kpo5tn">
          <p className="font-display text-xl font-semibold leading-snug text-ink sm:text-2xl" data-id="element-224" data-mp="1vf7kfgsgc9fd">
            Non una raccolta casuale di esercizi.
          </p>
          <p className="mt-2 font-sans text-base leading-relaxed text-ink-muted" data-id="element-225" data-mp="20gwxt335cz6m">
            PALLAMANO 360™ organizza il lavoro attraverso:
          </p>
          <ol className="mx-auto mt-5 flex max-w-xs flex-col items-stretch gap-1.5" aria-label="La logica del sistema" data-id="element-226" data-mp="3buqxwalkjw0p">
            {systemFlow.map((step, index) => <li key={step} className="flex flex-col items-center gap-1.5" data-id="element-227" data-mp="3fxf9gq1o3h1d" data-mpi={index}>
                <span className={`w-full rounded-lg px-4 py-2.5 font-display text-sm font-semibold uppercase tracking-[0.12em] text-paper-soft ${accentBg[volumes[index]?.accent ?? 'pine']}`} data-id="element-228" data-mp="1gozp5cur99pc">
                  {step}
                </span>
                {index < systemFlow.length - 1 ? <ArrowDownIcon className="h-4 w-4 text-ink-muted" strokeWidth={2.4} aria-hidden="true" data-id="element-229" data-mp="1kth9pe8xvr16" /> : null}
              </li>)}
          </ol>
          <p className="mt-5 font-sans text-base leading-relaxed text-ink-muted" data-id="element-230" data-mp="x0h6le498ujm">
            Apri qualsiasi volume e vai direttamente al lavoro che vuoi
            sviluppare.
          </p>
        </div>

        <ol className="mt-12 space-y-3" data-id="element-231" data-mp="16uhm89x5xi6l">
          {volumes.map((volume, index) => {
          const isOpen = openIndex === index;
          return <li key={volume.code} className={`overflow-hidden rounded-2xl border-2 bg-paper-soft transition-colors duration-150 ease-out ${isOpen ? accentBorder[volume.accent] : 'border-line'}`} data-id="element-232" data-mp="ebkb56x4fvzt" data-mpi={index}>
                <h3 data-id="element-233" data-mp="2l0chm1btg468">
                  <button type="button" onClick={() => setOpenIndex(isOpen ? null : index)} aria-expanded={isOpen} aria-controls={`volume-panel-${index}`} className="flex w-full items-stretch gap-4 text-left focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-pine" data-id="element-234" data-mp="2kz1gyado3cjc">
                    {/* Dorso del libro, come nel mockup. */}
                    <span className={`flex w-16 shrink-0 flex-col items-center justify-center py-4 text-paper-soft sm:w-20 ${accentBg[volume.accent]}`} aria-hidden="true" data-id="element-235" data-mp="34obsrdaaldc6">
                      <span className="font-display text-[11px] font-medium uppercase tracking-[0.18em]" data-id="element-236" data-mp="2uqnzkrsc4dw4">
                        Vol.
                      </span>
                      <span className="font-display text-3xl font-bold leading-none" data-id="element-237" data-mp="2znnxugiy0cco">
                        {Number(volume.code)}
                      </span>
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col justify-center py-4" data-id="element-238" data-mp="1obwt88kg00up">
                      <span className="block font-display text-xl font-bold uppercase leading-tight text-ink sm:text-2xl" data-id="element-239" data-mp="23ks57gmbq85q">
                        {volume.name}
                      </span>
                      <span className={`mt-1 block font-sans text-sm font-semibold ${accentText[volume.accent]}`} data-id="element-240" data-mp="1usq73ozq93ym">
                        {volume.count}
                      </span>
                    </span>
                    <span className="flex items-center pr-5" data-id="element-241" data-mp="2mhjo3oxyel99">
                      <ChevronDownIcon className={`h-5 w-5 shrink-0 text-ink-muted transition-transform duration-200 ease-out ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-242" data-mp="2igpxuox1gud7" />
                    </span>
                  </button>
                </h3>

                <div id={`volume-panel-${index}`} className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`} data-id="element-243" data-mp="28itpu5zeefjb">
                  <div className="overflow-hidden" data-id="element-244" data-mp="x5zld0llkr9a">
                    <div className="border-t border-line px-5 py-5 sm:px-7" data-id="element-245" data-mp="n0rbh0u65khb">
                      <div className="space-y-3" data-id="element-246" data-mp="ryt4adhecy5z">
                        {volume.intro.map(paragraph => <p key={paragraph} className="font-sans text-base leading-relaxed text-ink-muted" data-id="element-247" data-mp="rwq26v2yzekw">
                            {paragraph}
                          </p>)}
                      </div>

                      <div className="mt-4 grid gap-3 sm:grid-cols-2" data-id="element-248" data-mp="15qhe2a46bajs">
                        <div className="rounded-xl bg-paper-deep p-4" data-id="element-249" data-mp="15q7q0s2ovdgq">
                          <p className="font-display text-sm font-semibold uppercase tracking-[0.16em] text-rust" data-id="element-250" data-mp="1ao96d58qcyoi">
                            Risolve
                          </p>
                          <p className="mt-2 font-sans text-sm leading-relaxed text-ink" data-id="element-251" data-mp="1ko019817dfp4">
                            {volume.solves}
                          </p>
                        </div>
                        <div className="rounded-xl bg-pine-light p-4" data-id="element-252" data-mp="3mel9eixu7jtb">
                          <p className="font-display text-sm font-semibold uppercase tracking-[0.16em] text-pine" data-id="element-253" data-mp="3mgxxldu6uqet">
                            Risultato
                          </p>
                          <div className="mt-2 space-y-2" data-id="element-254" data-mp="39hf3tmxpero1">
                            {volume.result.map(line => <p key={line} className="font-sans text-sm leading-relaxed text-ink" data-id="element-255" data-mp="3odwdm9q8vy8h">
                                {line}
                              </p>)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </li>;
        })}
        </ol>
      </div>
    </section>;
}